import { useEffect, useState } from "react";
import { apiClient } from "../api/client.js";
import { useViewScope, type ViewScope } from "../auth/view-scope.js";
import type { AdminUser } from "../api/types.js";

// Vaihe UM.6.B.2 — view-scope switcher (admin-only).
// Sijaitsee nav:issa. Klikkaus → dropdown:
//   • Omat tiedostot (self)
//   • Admin: Kaikki
//   • Admin: <kayttaja> ▼

function scopeLabel(scope: ViewScope): string {
  switch (scope.kind) {
    case "self":
      return "Omat tiedostot";
    case "all":
      return "Admin: Kaikki";
    case "user":
      return `Admin: ${scope.label}`;
  }
}

export function ViewScopeSwitcher() {
  const { scope, setScope } = useViewScope();
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Lataa kayttajalistaa kun dropdown avataan (lazy)
  useEffect(() => {
    if (!open || users.length > 0) return;
    setLoadingUsers(true);
    apiClient
      .listUsers()
      .then((res) => setUsers(res.users))
      .catch(() => setUsers([]))
      .finally(() => setLoadingUsers(false));
  }, [open, users.length]);

  function selectScope(next: ViewScope) {
    setScope(next);
    setOpen(false);
    // Pakota reload jotta /me ja muut data uudelleen-haetaan oikealla scope:lla.
    // (V2: react-query / SWR invalidoi cachet)
    window.location.reload();
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${
          scope.kind === "self"
            ? "border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800"
            : "border-rose-700 bg-rose-900/40 text-rose-200 hover:bg-rose-900/60"
        }`}
      >
        {scopeLabel(scope)} ▾
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-1 w-72 rounded-md border border-slate-700 bg-slate-950 shadow-xl">
          <button
            type="button"
            onClick={() => selectScope({ kind: "self" })}
            className="block w-full px-4 py-2 text-left text-sm text-slate-200 hover:bg-slate-800"
          >
            Omat tiedostot
          </button>
          <div className="border-t border-slate-800" />
          <div className="px-4 py-1 text-xs font-medium uppercase tracking-wide text-slate-500">
            Admin-näkymät
          </div>
          <button
            type="button"
            onClick={() => selectScope({ kind: "all" })}
            className="block w-full px-4 py-2 text-left text-sm text-slate-200 hover:bg-slate-800"
          >
            Kaikki tiedostot (bypass)
          </button>
          <div className="border-t border-slate-800" />
          <div className="px-4 py-1 text-xs font-medium uppercase tracking-wide text-slate-500">
            Simuloi käyttäjää
          </div>
          <div className="max-h-64 overflow-y-auto">
            {loadingUsers ? (
              <div className="px-4 py-2 text-sm text-slate-500">Ladataan…</div>
            ) : users.length === 0 ? (
              <div className="px-4 py-2 text-sm text-slate-500">Ei käyttäjiä</div>
            ) : (
              users.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() =>
                    selectScope({ kind: "user", userId: u.id, label: u.name })
                  }
                  className="block w-full px-4 py-2 text-left text-sm text-slate-200 hover:bg-slate-800"
                >
                  <div>{u.name}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Banner kun scope != self. Tulee Nav:n alle. */
export function ViewScopeBanner() {
  const { scope, setScope } = useViewScope();
  if (scope.kind === "self") return null;
  const text =
    scope.kind === "all"
      ? "Katselet kaikkien käyttäjien projekteja (admin-bypass)."
      : `Katselet käyttäjän ${scope.label} näkymää (admin act-as).`;
  return (
    <div className="border-b border-rose-900 bg-rose-950/40">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-2 text-xs text-rose-200">
        <span>{text}</span>
        <button
          type="button"
          onClick={() => {
            setScope({ kind: "self" });
            window.location.reload();
          }}
          className="rounded border border-rose-700 px-2 py-0.5 text-rose-200 hover:bg-rose-900/60"
        >
          Palaa omiin
        </button>
      </div>
    </div>
  );
}
